import { CardLayout } from "@components/progress-overview/card-layout";
import { ErrorCard } from "@components/progress-overview/error-card";
import { FileUploadTask } from "@custom-types/file-upload-types";
import { neutral, RemainingTimeLabel } from "@flat-ui";
import { BackgroundTaskState } from "@faro-lotv/service-wires";
import { useCancelUpload } from "@hooks/upload-tasks/use-cancel-upload";
import { LinearProgress, Stack } from "@mui/material";
import { getProjectByIdSelector } from "@store/projects/projects-selector";
import { useAppSelector } from "@store/store-helper";
import { colorConst } from "@styles/common-colors";
import { DEFAULT_TEXT_FONT_SIZE } from "@styles/common-styles";
interface TaskCardProps {
  /** The task to show */
  task: FileUploadTask;
  /** True to show dev mode features */
  isDevMode?: boolean;
}

/** Renders a task in the menu card to notify the user of a progress item */
export function ProgressCard({ task, isDevMode }: TaskCardProps): JSX.Element {
  const project = useAppSelector(
    getProjectByIdSelector(task.context.projectId)
  );
  const cancelUploadTask = useCancelUpload();

  /** Return failed card if the task is failed */
  if (task.status === BackgroundTaskState.failed) {
    return <ErrorCard task={task} />;
  }

  return (
    <CardLayout
      name={task.fileName}
      projectName={project?.name}
      menu={[
        {
          name: "Cancel Upload",
          // We don't need to await the async cancelling, we just need to trigger it.
          callback: () => void cancelUploadTask(task.id),
          color: "error",
        },
      ]}
    >
      <LinearProgress
        value={task.progress}
        variant={task.progress > 0 ? "determinate" : "indeterminate"}
      />

      <Stack
        direction="row"
        alignItems="center"
        sx={{
          justifyContent: "space-between",
          fontSize: DEFAULT_TEXT_FONT_SIZE,
          color: `${neutral[800]}E6`,
        }}
      >
        <div>
          Uploading... &nbsp;
          {task.expectedEnd && (
            <>
              &nbsp;(
              <RemainingTimeLabel
                expectedEnd={task.expectedEnd}
                sx={{
                  color: `${neutral[800]}E6`,
                  fontWeight: "600",
                }}
              />
              )
            </>
          )}
        </div>
        <var style={{ color: isDevMode ? colorConst.devFeature : undefined }}>
          &nbsp;{" "}
          {isDevMode && task.speedMBps && `${task.speedMBps.toFixed(2)} MB/s`}
        </var>
      </Stack>
    </CardLayout>
  );
}
